import { createContext, useContext } from 'react';
import {
  AdviseEngagement,
  AdviseSupplier,
  DiscoveryAnswer,
  DiscoveryFormSection,
  MatrixCriterion,
  MatrixCriterionValue
} from '../../../../types/advise';

export interface EngagementDiscovery {
  // Null while the form loads, and after a load fails.
  sections: DiscoveryFormSection[] | null;
  failed?: boolean;
  retry?: () => void;
  // Replaces the question's whole answer and saves it in the background; does nothing on a read-only engagement.
  answer: (questionId: string, value: DiscoveryAnswer) => void;
}

export interface EngagementFunctionalityMatrix {
  // Null until the matrix first loads, and after a load fails.
  criteria: MatrixCriterion[] | null;
  failed?: boolean;
  // Loads the matrix once the edits still saving have landed, since a Discovery answer can change its selections.
  refresh?: () => void;
  // Each shows the change at once and saves it in the background; they do nothing on a read-only engagement.
  updateCriterion: (criterionId: string, value: MatrixCriterionValue) => void;
  selectFeature: (featureId: string, selected: boolean) => void;
}

export interface EngagementEditor {
  engagement: AdviseEngagement;
  // Every supplier; each panel picks the engagement's selected ones itself.
  suppliers: AdviseSupplier[];
  canEdit: boolean;
  // Shows the patch at once and saves it in the background; does nothing on a read-only engagement.
  editEngagement: (patch: Partial<AdviseEngagement>) => void;
  discovery: EngagementDiscovery;
  functionalityMatrix: EngagementFunctionalityMatrix;
}

const EngagementEditorContext = createContext<EngagementEditor | null>(null);

export const EngagementEditorProvider = EngagementEditorContext.Provider;

/** The engagement, its suppliers and how to edit it, from the nearest EngagementEditorProvider. */
export const useEngagementEditor = (): EngagementEditor => {
  const editor = useContext(EngagementEditorContext);
  if (!editor) throw new Error('useEngagementEditor must be used inside an EngagementEditorProvider');
  return editor;
};
